/**
 * @Author Morsal
 */
import { GameState } from "@shared/types";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { css, html } from "../helpers/webComponents";
import { Page } from "../enums/Page";
import { GetUsernameResponse, UsernameService } from "../services/UsernameService";

export class WorldMapComponent extends HTMLElement {
    private readonly _gameRouteService = new GameRouteService();
    private readonly _gameEventService = new GameEventService();
    private _usernameService = new UsernameService();
    private _hasUsername = false;
    private _showGlitchTransition = false;

    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });
        try {
            const res: GetUsernameResponse = await this._usernameService.getUsername();
            this._hasUsername = res.username !== null;
        }
        catch (err) {
            console.warn("Failed to fetch username:", err);
            this._hasUsername = false;
        }

        window.addEventListener("username:entered", () => {
            this._hasUsername = true;
            this._showGlitchTransition = true;

            this.render();

            setTimeout(() => {
                this._showGlitchTransition = false;
                this.render();
            }, 1500); // glitch duur in ms
        });

        this.render();
    }

    private render(): void {
        if (!this.shadowRoot) return;

        const style: string = css`
            :host {
                display: block;
                width: auto;
                height: 100%;
            }
    
            .map-container {
                position: relative;
                width: 100%;
                max-width: 1000px;
                margin: auto;
            }

            .map-image {
                width: 100%;
                height: auto;
                display: block;
            }

            .city-button {
                position: absolute;
                width: 80px;
                height: 30px;
                background: transparent;
                border: none;
                cursor: pointer;
                z-index: 2;
            }

            .city-button:hover::after {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                border: 2px solid #00eaff;
                box-shadow: 0 0 10px #00eaff;
                border-radius: 6px;
                animation: pulse 1s infinite;
            }
            .glitching {
                animation: glitchFlash 0.6s linear, scanlines 0.6s linear;
                filter: contrast(1.3) brightness(1.1);
                mix-blend-mode: screen;
                position: relative;
                z-index: 2;
            }

            .tv-static-overlay {
                position: absolute;
                inset: 0;
                background: url("/assets/img/rooms/worldmap/tv-glitch.png") center center / cover no-repeat;
                opacity: 0;
                z-index: 5;
                pointer-events: none;
                animation: showStatic 0.6s steps(10) forwards;
            }


            @keyframes fadeInMap {
                to {
                    opacity: 1;
                }
            }
            @keyframes showStatic {
                0%, 100% { opacity: 0; }
                10% { opacity: 0.9; }
                20% { opacity: 0.2; }
                30% { opacity: 1; }
                40% { opacity: 0.4; }
                50% { opacity: 0.8; }
                60% { opacity: 0.1; }
                70% { opacity: 0.95; }
                80% { opacity: 0.3; }
                90% { opacity: 1; }
            }

            @keyframes pulse {
                0% { box-shadow: 0 0 10px #00eaff; }
                50% { box-shadow: 0 0 20px #00eaff; }
                100% { box-shadow: 0 0 10px #00eaff; }
            }
            
        `;

        const cities: {
            name: string;
            alias: string;
            x: number;
            y: number;
            width?: number;
            height?: number;
        }[] = [
            { name: "Las Vegas", alias: "lasvegas", x: 120, y: 160, width: 170, height: 50 },
            { name: "Sydney", alias: "sydney", x: 230, y: 85, width: 110, height: 50 },
            { name: "Washington", alias: "washington", x: 375, y: 160, width: 170, height: 50 },
            { name: "Tokyo", alias: "tokyo", x: 680, y: 170, width: 130, height: 50 },
            { name: "Moscow", alias: "moscow", x: 730, y: 240, width: 100, height: 50 },
            { name: "London", alias: "london", x: 350, y: 310, width: 90, height: 50 },
            { name: "Dublin", alias: "dublin", x: 540, y: 270, width: 90, height: 40 },
            { name: "Jakarta", alias: "jakarta", x: 585, y: 330, width: 140, height: 50 },
            { name: "Rio de Janeiro", alias: "riodejaneiro", x: 100, y: 390, width: 260, height: 50 },
            { name: "Marrakesh", alias: "marrakesh", x: 310, y: 580, width: 190, height: 60 },
            { name: "Atlantis", alias: "atlantis", x: 700, y: 510, width: 180, height: 60 },
        ];

        const container: HTMLDivElement = document.createElement("div");
        container.className = "map-container";

        const img: HTMLImageElement = document.createElement("img");
        img.alt = "World Map";
        img.className = "map-image";

        if (!this._hasUsername) {
            img.src = "/assets/img/rooms/worldmap/tv-empty.png";
        }
        else {
            img.src = "/assets/img/rooms/worldmap/worldmapp.png";
        }

        if (!this._hasUsername) {
            // geen ruis tonen als er nog geen username is
        }
        else {
            const staticOverlay: HTMLDivElement = document.createElement("div");
            staticOverlay.className = "tv-static-overlay";
            container.appendChild(staticOverlay);
        }

        const buttonLayer: HTMLDivElement = document.createElement("div");
        buttonLayer.style.position = "absolute";
        buttonLayer.style.inset = "0";

        if (!this._showGlitchTransition && this._hasUsername) {
            cities.forEach(city => {
                const btn: HTMLButtonElement = document.createElement("button");
                btn.className = "city-button";
                btn.textContent = "";
                const originalWidth: number = 1000;
                const originalHeight: number = 800;

                btn.style.left = `${(city.x / originalWidth) * 100}%`;
                btn.style.top = `${(city.y / originalHeight) * 100}%`;
                btn.style.width = `${(city.width ?? 100) / originalWidth * 100}%`;
                btn.style.height = `${(city.height ?? 35) / originalHeight * 100}%`;
                btn.title = city.name; // tooltip bij hover
                btn.addEventListener("click", () => this.selectCity(city.alias));
                buttonLayer.appendChild(btn);
            });
        }

        container.appendChild(img);
        container.appendChild(buttonLayer);

        this.shadowRoot.innerHTML = "";
        this.shadowRoot.appendChild(html`<style>${style}</style>`);
        this.shadowRoot.appendChild(container);
    }

    private async selectCity(cityAlias: string): Promise<void> {
        const result: GameState | undefined = await this._gameRouteService.executeAction(cityAlias);

        if (!result) return;

        if (result.type === "switch-page") {
            this._gameEventService.switchPage(result.page as Page);
        }
        else {
            window.dispatchEvent(
                new CustomEvent("game:update-state", {
                    detail: result,
                })
            );
        }
    }
}
